import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, FileText, Brain, CheckCircle2, FileCheck, AlertCircle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHelpers";
import ExtractionProgress from "../components/ExtractionProgress";
import UploadProgress, { FileStatus } from "../components/UploadProgress";
import { uploadFilesChunked, startExtraction } from "../utils/uploadChunked";
import { uploadFilesToGCS } from "../utils/gcsUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const WORKFLOW_STAGES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  EXTRACTING: 'extracting',
  EXTRACTION_COMPLETE: 'extraction_complete',
  READY_FOR_FORMAT_SELECTION: 'ready_for_format_selection',
  GENERATING_OPINION: 'generating_opinion'
};

// Format duration in ms to readable string
const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Live timer component
const LiveTimer = ({ startTime, label }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    const updateElapsed = () => setElapsed(Date.now() - startTime);
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  return (
    <div className="flex items-center gap-2 text-blue-600">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span className="text-sm font-medium">{label}</span>
      <span className="font-mono font-bold">{formatDuration(elapsed)}</span>
    </div>
  );
};

/**
 * Calculate dynamic timeout based on file count and total size
 * 
 * Based on backend analysis:
 * - LLM: gemini-2.0-flash (~20 sec per doc)
 * - Concurrency: 5 parallel extractions
 * - Per-doc timeout: 45 seconds in backend
 * 
 * @param {number} fileCount - Number of files to process
 * @param {number} totalSizeMB - Total size in MB
 * @returns {number} Timeout in milliseconds
 */
const calculateDynamicTimeout = (fileCount, totalSizeMB) => {
  const LLM_TIME_PER_DOC = 20 * 1000;      // 20 sec per LLM call
  const PARALLEL_FACTOR = 5;               // 5 concurrent extractions (matches backend)
  const SIZE_FACTOR = 3 * 1000;            // 3 sec per MB (OCR, parsing overhead)
  const BASE_OVERHEAD = 30 * 1000;         // 30 sec base overhead (network, init)
  const BUFFER = 1.5;                      // 50% safety buffer
  
  // Calculate batches needed with parallelization
  const parallelBatches = Math.ceil(fileCount / PARALLEL_FACTOR);
  const llmTime = parallelBatches * LLM_TIME_PER_DOC;
  const sizeTime = totalSizeMB * SIZE_FACTOR;
  
  const timeout = Math.ceil((BASE_OVERHEAD + llmTime + sizeTime) * BUFFER);
  
  // Minimum 60 seconds, maximum 30 minutes
  const MIN_TIMEOUT = 60 * 1000;
  const MAX_TIMEOUT = 30 * 60 * 1000;
  
  return Math.max(MIN_TIMEOUT, Math.min(timeout, MAX_TIMEOUT));
};

/**
 * Format timeout for display
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {string} Human readable time
 */
const formatTimeout = (timeoutMs) => {
  const seconds = Math.ceil(timeoutMs / 1000);
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};

// Add axios interceptor to include token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const HomePage = ({ selectedState = null, stateInfo = null, workflow = 'retail' }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [showJobProgress, setShowJobProgress] = useState(false);
  
  // Chunked upload state
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [uploadProgressData, setUploadProgressData] = useState({ completed: 0, total: 0, percent: 0 });
  const [uploadError, setUploadError] = useState(null);
  
  // Upload timer state
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [extractStartTime, setExtractStartTime] = useState(null);
  const [workflowStage, setWorkflowStage] = useState(WORKFLOW_STAGES.IDLE);
  const [stageMessage, setStageMessage] = useState('');
  const [stageDetail, setStageDetail] = useState('');
  const terminalStageReachedRef = useRef(false);
  
  // Tenant-allowed formats state
  const [allowedFormats, setAllowedFormats] = useState({ retail: [], apf: [] });
  
  const navigate = useNavigate();

  // Fetch tenant-allowed formats on mount
  useEffect(() => {
    const fetchAllowedFormats = async () => {
      try {
        const [retailRes, apfRes] = await Promise.all([
          axios.get(`${API}/bank-formats?category=RETAIL`),
          axios.get(`${API}/bank-formats?category=APF`)
        ]);
        setAllowedFormats({
          retail: retailRes.data.formats || [],
          apf: apfRes.data.formats || []
        });
      } catch (error) {
        console.error("Error fetching allowed formats:", error);
      }
    };
    fetchAllowedFormats();
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file count (max 200 files)
    if (selectedFiles.length > 200) {
      toast.error("Maximum 200 files allowed per upload");
      return;
    }
    
    // Validate file sizes (max 200MB per file)
    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes
    const invalidFiles = [];
    const validFiles = [];
    
    selectedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (invalidFiles.length > 0) {
      toast.error(`Files exceed 200MB limit: ${invalidFiles.join(', ')}`);
    }
    
    if (validFiles.length > 0) {
      setFiles(validFiles);
      toast.success(`${validFiles.length} file(s) selected (all file types accepted)`);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    console.log("🚀 Starting direct GCS upload for", files.length, "files");
    setUploading(true);
    setUploadProgress(0);
    
    const allUploadedDocs = [];

    try {
      const token = localStorage.getItem('token');
      
      // Use direct GCS upload (bypasses Cloud Run 32MB limit)
      const results = await uploadFilesToGCS(files, token, (progress) => {
        console.log(`📤 Upload progress:`, progress);
        setUploadProgress(progress.percent || 0);
        if (progress.filename) {
          toast.info(`Uploading: ${progress.filename} (${progress.current}/${progress.total})`, {
            id: 'upload-progress',
            duration: 2000
          });
        }
      });
      
      // Collect successful uploads
      for (const result of results) {
        if (result.success) {
          allUploadedDocs.push(result.document);
        } else {
          toast.error(`Failed to upload ${result.filename}: ${result.error}`);
        }
      }
      
      setUploadedDocs(allUploadedDocs);
      
      if (allUploadedDocs.length > 0) {
        toast.success(`✅ ${allUploadedDocs.length} document(s) uploaded successfully!`);
      }
      
      if (allUploadedDocs.length < files.length) {
        toast.warning(`${files.length - allUploadedDocs.length} file(s) failed to upload`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = getErrorMessage(error, "Failed to upload documents. Please try again.");
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentBatch(0);
    }
  };

  const handleExtract = async () => {
    // ============================================================
    // SIMPLE SYNCHRONOUS FLOW (Fast & Direct)
    // Upload files -> Extract immediately -> Get result
    // No background jobs, no polling - just wait for response
    // ============================================================
    
    terminalStageReachedRef.current = false;

    // If documents already uploaded via old flow, extract them directly
    if (uploadedDocs.length > 0) {
      setExtracting(true);
      setExtractionProgress(0);
      setWorkflowStage(WORKFLOW_STAGES.EXTRACTING);
      setStageMessage('Extracting data');
      setStageDetail('Processing uploaded documents...');
      const documentIds = uploadedDocs.map((doc) => doc.id);
      
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
          `${API}/documents/extract`, 
          {
            document_ids: documentIds,
            state_code: selectedState,
            workflow_type: workflow
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 600000 // 10 minute timeout for large batches
          }
        );
        
        const extractionId = response.data.extracted_data.id;
        toast.success(`✅ Data extracted successfully!`);
        terminalStageReachedRef.current = true;
        setWorkflowStage(WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION);
        setStageMessage('Ready for format selection');
        setStageDetail('Extraction is complete. Choose APF or retail on the next screen.');
        navigate(`/extract/${extractionId}`, {
          state: {
            workflowStage: WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION,
            stageMessage: 'Extraction complete. Select a format to continue.'
          }
        });
      } catch (error) {
        console.error(error);
        const errorMsg = getErrorMessage(error, "Failed to extract data. Please try again.");
        toast.error(errorMsg);
      } finally {
        setExtracting(false);
        setExtractionProgress(0);
        if (!terminalStageReachedRef.current) {
          setWorkflowStage(WORKFLOW_STAGES.IDLE);
          setStageMessage('');
          setStageDetail('');
        }
      }
      return;
    }

    // New files selected - upload first, then extract
    if (files.length === 0) {
      toast.error("Please select files first");
      return;
    }

    if (!selectedState) {
      toast.error("State not selected. Please go back and select a state.");
      return;
    }

    // =================================================================
    // 520 RECOVERY TRACKING (February 2025)
    // These variables must be declared OUTSIDE try block so they're
    // accessible in the catch block for proxy timeout recovery
    // =================================================================
    let extractionInitiated = false;
    let initiatedJobId = null;

    try {
      // Phase 1: Upload files
      setShowUploadProgress(true);
      setUploading(true);
      setUploadError(null);
      setFileStatuses({});
      setUploadProgressData({ completed: 0, total: files.length, percent: 0 });
      setUploadStartTime(Date.now()); // Start upload timer
      setWorkflowStage(WORKFLOW_STAGES.UPLOADING);
      setStageMessage('Uploading documents');
      setStageDetail('Preparing files for extraction...');

      console.log(`🚀 Starting upload for ${files.length} files`);

      // Upload files using chunked upload (for reliability)
      const uploadResult = await uploadFilesChunked(files, {
        chunkSize: 1,
        onFileStart: ({ file, index }) => {
          console.log(`📤 Uploading: ${file.name} (${index + 1}/${files.length})`);
          setFileStatuses(prev => ({
            ...prev,
            [file.name]: FileStatus.UPLOADING
          }));
        },
        onFileComplete: ({ file, fileId, completed }) => {
          console.log(`✅ Uploaded: ${file.name} -> ${fileId}`);
          setFileStatuses(prev => ({
            ...prev,
            [file.name]: FileStatus.COMPLETE
          }));
        },
        onFileError: ({ file, error }) => {
          console.error(`❌ Failed: ${file.name} - ${error}`);
          setFileStatuses(prev => ({
            ...prev,
            [file.name]: FileStatus.ERROR
          }));
        },
        onProgress: (progress) => {
          setUploadProgressData(progress);
        }
      });

      console.log(`📦 Upload complete: ${uploadResult.totalUploaded} files`);
      setUploadStartTime(null); // Clear upload timer
      setWorkflowStage(WORKFLOW_STAGES.EXTRACTING);
      setStageMessage('Extracting data');
      setStageDetail(`Starting extraction for ${uploadResult.totalUploaded} document(s)...`);

      if (uploadResult.fileIds.length === 0) {
        throw new Error('All files failed to upload');
      }

      // Phase 2: Start extraction (returns immediately with job_id)
      setShowUploadProgress(false);
      setExtracting(true);
      setExtractStartTime(Date.now()); // Start extraction timer
      
      // Calculate total file size in MB for dynamic timeout
      const totalSizeMB = files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
      const fileCount = uploadResult.fileIds.length;
      
      // Calculate dynamic timeout based on file count and size
      const dynamicTimeout = calculateDynamicTimeout(fileCount, totalSizeMB);
      const estimatedTimeFormatted = formatTimeout(dynamicTimeout);
      
      console.log(`📊 Dynamic timeout calculated: ${dynamicTimeout}ms (${estimatedTimeFormatted}) for ${fileCount} files, ${totalSizeMB.toFixed(2)}MB`);
      
      toast.info(`Files uploaded! Starting extraction for ${fileCount} documents...`, { duration: 5000 });

      console.log(`🔍 Starting extraction for ${fileCount} files`);

      // Use extraction endpoint - handles both SYNC and ASYNC modes based on server config
      // Step 1: Start extraction job
      extractionInitiated = true;
      const jobResult = await startExtraction(uploadResult.fileIds, {
        workflowType: workflow,
        stateCode: selectedState
      });

      initiatedJobId = jobResult.job_id;
      console.log(`📋 Extraction job accepted: ${initiatedJobId}`);
      
      // =================================================================
      // SEQUENTIAL ASYNC MODE ONLY (February 2025)
      // All extractions run in background - poll for completion
      // =================================================================
      let extractionId = null;
      const estimatedSeconds = jobResult.estimated_time_seconds || fileCount * 12;
      
      console.log(`⏳ [SEQUENTIAL ASYNC] Job accepted, estimated time: ${estimatedSeconds}s`);
      toast.info(`Processing ${fileCount} files. Estimated time: ~${Math.ceil(estimatedSeconds/60)} min...`, { duration: 5000 });
      
      // Poll for completion
      const token = localStorage.getItem('token');
      let pollCount = 0;
      const maxPolls = 180; // Max 15 minutes
      const pollInterval = 5000; // 5 seconds
      
      while (!extractionId && pollCount < maxPolls) {
        pollCount++;
        
        try {
          const statusResponse = await axios.get(
            `${API}/extraction-result/${initiatedJobId}`,
            {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 10000
              }
            );
            
            const status = statusResponse.data;
            const progress = status.progress || {};

            if (terminalStageReachedRef.current) {
              break;
            }
            
            if (status.status === 'completed') {
              extractionId = status.extraction_id;
              terminalStageReachedRef.current = true;
              setWorkflowStage(WORKFLOW_STAGES.EXTRACTION_COMPLETE);
              setStageMessage('Extraction complete');
              setStageDetail('Opening format selection...');
              setExtracting(false);
              setExtractStartTime(null);
              console.log(`✅ [SEQUENTIAL ASYNC] Extraction completed: ${extractionId}`);
              toast.success(`All ${fileCount} files processed!`, { duration: 2000 });
            } else if (status.status === 'failed') {
              throw new Error(status.error || 'Extraction failed');
            } else {
              setWorkflowStage(WORKFLOW_STAGES.EXTRACTING);
              // Show progress with file info
              if (progress.current && progress.total) {
                const progressPercent = progress.percent || Math.round((progress.current / progress.total) * 100);
                const currentFile = progress.current_file || `file ${progress.current}`;
                setExtractionProgress(progressPercent);
                setStageMessage('Extracting data');
                setStageDetail(`Processed ${progress.current}/${progress.total}: ${currentFile}`);
                console.log(`📊 Poll ${pollCount}: ${progress.current}/${progress.total} (${progressPercent}%) - ${currentFile}`);
                
                // Update toast every 3rd poll (15 seconds)
                if (pollCount % 3 === 0) {
                  toast.info(`Processing ${progress.current}/${progress.total}: ${currentFile}`, { duration: 4000 });
                }
              } else {
                setStageMessage('Extracting data');
                setStageDetail('Processing uploaded documents...');
                console.log(`📊 Poll ${pollCount}: Job ${initiatedJobId} status: ${status.status}`);
              }
            }
          } catch (pollError) {
            if (pollError.response?.status === 404) {
              console.warn(`Job ${initiatedJobId} not found, may still be initializing...`);
            } else if (pollError.message?.includes('failed')) {
              throw pollError; // Re-throw extraction failures
            } else {
              console.warn('Poll error:', pollError.message);
            }
          }
          
          // Wait before next poll if not complete
          if (!extractionId) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
        }
        
        if (!extractionId) {
          throw new Error('Extraction timed out. Please check the extractions page for results.');
        }

      console.log(`✅ Extraction complete: ${extractionId}`);

      toast.success("✅ Data extracted successfully!");
      setExtracting(false);
      setUploading(false);
      setExtractStartTime(null); // Clear extraction timer
      setWorkflowStage(WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION);
      setStageMessage('Ready for format selection');
      setStageDetail('Extraction is complete. Choose APF or retail on the next screen.');
      
      // Navigate to extraction page
      navigate(`/extract/${extractionId}`, {
        state: {
          workflowStage: WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION,
          stageMessage: 'Extraction complete. Select a format to continue.'
        }
      });

    } catch (error) {
      console.error('Extraction flow error:', error);
      
      // =================================================================
      // ERROR RECOVERY (February 2025)
      // =================================================================
      // If extraction was initiated but we got an error, try to recover
      // by checking job status - the extraction may have completed
      // =================================================================
      
      if (extractionInitiated && initiatedJobId) {
        console.log(`⚠️ [RECOVERY] Error occurred, checking job ${initiatedJobId} status...`);
        
        try {
          const token = localStorage.getItem('token');
          const statusResponse = await axios.get(
            `${API}/extraction-result/${initiatedJobId}`,
            {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 10000
            }
          );
          
          if (statusResponse.data.status === 'completed' && statusResponse.data.extraction_id) {
            const recoveredExtractionId = statusResponse.data.extraction_id;
            console.log(`✅ [RECOVERY] Found completed extraction: ${recoveredExtractionId}`);
            toast.success('Extraction recovered! Redirecting...', { duration: 2000 });
            terminalStageReachedRef.current = true;
            
            setShowUploadProgress(false);
            setExtracting(false);
            setUploading(false);
            setExtractStartTime(null);
            setWorkflowStage(WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION);
            setStageMessage('Ready for format selection');
            setStageDetail('Recovered completed extraction. Opening format selection...');
            
            navigate(`/extract/${recoveredExtractionId}`, {
              state: {
                workflowStage: WORKFLOW_STAGES.READY_FOR_FORMAT_SELECTION,
                stageMessage: 'Extraction complete. Select a format to continue.'
              }
            });
            return;
          }
        } catch (recoveryError) {
          console.error('Recovery check failed:', recoveryError);
        }
      }
      
      // Normal error handling
      setShowUploadProgress(false);
      setExtracting(false);
      setUploading(false);
      setUploadStartTime(null);
      setExtractStartTime(null);
      setWorkflowStage(WORKFLOW_STAGES.IDLE);
      setStageMessage('');
      setStageDetail('');
      
      const errorMsg = getErrorMessage(error, "Failed to process documents. Please try again.");
      toast.error(errorMsg);
      setUploadError(errorMsg);
    }
  };

  const handleJobComplete = (job) => {
    console.log("✅ Job completed:", job);
    
    // NEW: Both APF and Retail workflows return extraction_id
    // User will be redirected to extraction page to:
    // 1. View extracted data
    // 2. Select bank format (APF formats or Retail formats based on workflow)
    // 3. Generate opinion
    
    const extractionId = job.result?.extraction_id || job.extraction_id;
    
    if (extractionId) {
      // UNIFIED FLOW: Navigate to extraction page for format selection
      toast.success("✅ Data extracted! Redirecting to review page...");
      
      setTimeout(() => {
        setShowJobProgress(false);
        navigate(`/extract/${extractionId}`);
      }, 1500);
    } else {
      // Legacy fallback: check if opinion was auto-generated (old APF behavior)
      const hasOpinionUrl = job.opinion_url || job.opinion_file_path;
      if (hasOpinionUrl) {
        toast.success("✅ Opinion generated! Downloading...");
        const downloadUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/download_opinion/${job.job_id}`;
        window.location.href = downloadUrl;
        setTimeout(() => {
          setShowJobProgress(false);
        }, 2000);
      } else {
        toast.error("Job completed but no output found");
        setShowJobProgress(false);
      }
    }
  };

  const handleJobCancel = () => {
    setShowJobProgress(false);
    setCurrentJobId(null);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>TechOrange-Legal</h1>
                <p className="text-sm text-gray-600 mt-0.5">Professional Legal Document Automation</p>
              </div>
            </div>
            
            {/* State Badge */}
            {selectedState && stateInfo && (
              <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold">
                <span className="text-sm">📍 {stateInfo.name} ({selectedState})</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Generate Legal Opinions in Minutes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Upload property documents, extract data automatically with AI, and generate bank-compliant legal opinions with your digital signature.
            </p>
          </div>

          {/* Chunked Upload Progress Modal */}
          {showUploadProgress && (
            <UploadProgress
              files={files}
              fileStatuses={fileStatuses}
              progress={uploadProgressData}
              isUploading={uploading}
              error={uploadError}
              onCancel={() => {
                setShowUploadProgress(false);
                setUploading(false);
                toast.warning("Upload cancelled");
              }}
            />
          )}

          {/* Show Progress Component when job is running */}
          {showJobProgress && currentJobId ? (
            <ExtractionProgress
              jobId={currentJobId}
              onComplete={handleJobComplete}
              onCancel={handleJobCancel}
            />
          ) : (
            /* Upload Section */
            <Card data-testid="upload-section" className="shadow-xl border-2">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50">
              <CardTitle className="text-2xl">Start New Legal Opinion</CardTitle>
              <CardDescription>Upload your property documents to begin the automated process</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* File Input */}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-all">
                  <input
                    data-testid="file-input"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <Upload className="w-8 h-8 text-blue-700" />
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-700">Click to select files or drag & drop</p>
                        <p className="text-sm text-gray-500 mt-1">Supported formats: PDF, DOCX • Maximum 200 files</p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Selected Files */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-700 flex items-center">
                      <FileCheck className="w-4 h-4 mr-2 text-green-600" />
                      Selected Files ({files.length})
                    </p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {files.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between space-x-2 text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{file.name}</span>
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">{(file.size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Live Timer Display during Upload/Extraction */}
                {(uploadStartTime || extractStartTime) && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {uploadStartTime ? 'Uploading Documents' : 'Extracting Data'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {uploadStartTime ? 'Transferring files to cloud storage...' : 'AI processing your documents...'}
                          </p>
                        </div>
                      </div>
                      <LiveTimer 
                        startTime={uploadStartTime || extractStartTime}
                        label={uploadStartTime ? "Upload" : "Extraction"}
                      />
                    </div>
                  </div>
                )}

                {workflowStage !== WORKFLOW_STAGES.IDLE && !showUploadProgress && (
                  <div className="p-4 rounded-lg border bg-white shadow-sm" data-testid="workflow-stage-card">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Current stage</p>
                        <p className="text-lg font-semibold text-gray-900" data-testid="workflow-stage-label">{workflowStage}</p>
                        {stageMessage && <p className="text-sm text-gray-700 mt-1">{stageMessage}</p>}
                        {stageDetail && <p className="text-sm text-gray-500 mt-1">{stageDetail}</p>}
                      </div>
                      {(workflowStage === WORKFLOW_STAGES.UPLOADING || workflowStage === WORKFLOW_STAGES.EXTRACTING) && (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Process Documents Button - NEW ASYNC FLOW */}
                <Button
                  data-testid="process-button"
                  onClick={handleExtract}
                  disabled={uploading || files.length === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6 text-lg font-semibold"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing Documents...
                    </>
                  ) : (
                    <>
                      <Brain className="w-5 h-5 mr-2" />
                      Process Documents with AI
                    </>
                  )}
                </Button>

                {/* Old progress bars removed - now using ExtractionProgress component */}
              </div>
            </CardContent>
          </Card>
          )}

          {/* Info Section */}
          <Card className="mt-8 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Available Bank Formats:</p>
                  {allowedFormats.retail.length > 0 && (
                    <p>Retail: {allowedFormats.retail.map(f => f.name).join(' • ')}</p>
                  )}
                  {allowedFormats.apf.length > 0 && (
                    <p>APF: {allowedFormats.apf.map(f => f.name).join(' • ')}</p>
                  )}
                  {allowedFormats.retail.length === 0 && allowedFormats.apf.length === 0 && (
                    <p className="text-gray-500">No formats available. Contact your administrator.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default HomePage;